// Displays information of a single Order in card format
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Subtitle2 } from "@fluentui/react-components";
import { Order, ApiException } from "@dining/api-client";
import { useNavigate } from "react-router-dom";
import { useStyles } from "./OrderHistoryCardStyles";
import {
  HelperUtils,
  appInsightsEvents,
  clearActiveCart,
  trackEvent,
  useAppStore,
} from "../../../common";
import { handleReorder } from "../../../common/reorderUtils";
import { ReusableDialog } from "../../shared/Reorder/ReorderDialog";
import { ConfirmationDialog, ConfirmationDialogProps, LoadingScreen } from "../../shared";
interface OrderCardProps {
  order?: Order;
}
export const OrderHistoryCard = ({ order }: OrderCardProps) => {
  const styles = useStyles();
  const [appInsights, context] = useAppStore((state) => [state.appInsights, state.context]);
  const helperUtils = HelperUtils.getInstance();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState<string | JSX.Element>("");
  const [dialogButtonText, setDialogButtonText] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogAction, setDialogAction] = useState<() => void>(() => () => {});
  const { getCart, value: currentCart, abandonCart } = useAppStore((state) => state.cart);
  const { reOrder, reorderingEnabled } = useAppStore((state) => state.ordersHistory);
  const itemListRef = useRef<HTMLDivElement>(null);
  const [adjustedItems, setAdjustedItems] = useState<string>("");
  const reOrderConsent = false;
  const orderHistory = useAppStore((state) => state.ordersHistory);
  const client = useAppStore((state) => state.client);
  const [dialogProps, setDialogProps] = useState<ConfirmationDialogProps>();

  useEffect(() => {
    trackEvent(appInsights, appInsightsEvents.Usage.ViewComponent, context, {
      componentName: "orderHistoryCard",
    });
  }, [context, appInsights]);

  const onReorder = useCallback(async () => {
    order?.cafeId !== currentCart?.cafeId && currentCart! ? emptyOtherCart() : handleReorderClick();
  }, [order?.cafeId, currentCart, client]);

  const formatDialogMessage = (items: { name: string }[]) => {
    if (items.length === 0) {
      return "";
    }
    if (items.length === 2) {
      return `${items[0].name} and ${items[1].name}`;
    } else if (items.length > 2) {
      const allButLastItem = items
        .slice(0, -1)
        .map((item) => item.name)
        .join(", ");
      const lastItem = items[items.length - 1].name;
      return `${allButLastItem} and ${lastItem}`;
    }
    return items[0].name;
  };

  const getContentStr = useCallback(
    async (cafeId: string | undefined) => {
      let cafeName: string | undefined;
      let contentStr: string | undefined = "";
      let allItemNames: { name: string }[] = [];
      allItemNames = (order?.items ?? []).map((item) => ({ name: item.name }));
      const itemNames = formatDialogMessage(allItemNames);
      try {
        const response = await client?.getCafeById(cafeId!, undefined);
        cafeName = response?.name;
      } catch (error) {
        if (ApiException.isApiException(error)) {
          const apiException = error as ApiException;
          appInsights.trackException(
            { exception: apiException },
            {
              method: "getCafeById",
              cafeId,
            },
          );
        } else {
          appInsights.trackException(
            { exception: error as Error },
            {
              method: "getCafeById",
              cafeId,
            },
          );
        }
      }

      contentStr = `You currently have an active cart from ${currentCart?.cafeName}. 
                        Do you wish to remove ${
                          currentCart && currentCart?.items?.length > 1
                            ? "those items"
                            : "that item"
                        } 
                        and add ${itemNames} from ${
                          cafeName ? cafeName : ""
                        } to your cart instead?`;
      return contentStr;
    },
    [order?.cafeId, client, currentCart],
  );

  const emptyOtherCart = useCallback(async () => {
    const contentStr = await getContentStr(order?.cafeId);

    setDialogProps({
      content: contentStr,
      open: true,
      title: clearActiveCart,

      confirmAction: async () => {
        trackEvent(
          appInsights,
          appInsightsEvents.Usage.Actions.AbandonCartDialog.AddItemToCartConfirmed,
          context,
          {
            cafeId: currentCart?.cafeId,
            cartId: currentCart?.id,
            cafeName: currentCart?.cafeName,
            component: "OrderHistoryCard",
          },
        );
        await abandonCart();
        handleReorderClick();
        setDialogProps(undefined);
      },
      dismissAction: () => {
        trackEvent(
          appInsights,
          appInsightsEvents.Usage.Actions.AbandonCartDialog.ItemOrderDismissed,
          context,
          {
            cafeId: currentCart?.cafeId,
            cartId: currentCart?.id,
            cafeName: currentCart?.cafeName,
            component: "OrderHistoryCard",
          },
        );
        setDialogProps(undefined);
      },
    });
  }, [setDialogProps, abandonCart, appInsights, context, currentCart]);

  const handleReorderClick = async () => {
    handleReorder({
      selected: order,
      orderId: order?.id,
      reOrder,
      reOrderConsent,
      setLoading,
      setDialogOpen,
      setDialogTitle,
      setDialogMessage,
      setDialogAction,
      setDialogButtonText,
      getCart,
      navigate,
    });
    await orderHistory.fetch();
  };

  const adjustCardItemNames = useCallback(() => {
    if (!order?.items) return;

    const itemNames = order.items
      .map((item) => {
        if (!item.name) return undefined;
        const label = helperUtils.getDietaryLabelStr(item);
        return label ? `${item.name} ${label}` : item.name;
      })
      .filter((n): n is string => Boolean(n));
    if (!itemNames.length || !itemListRef.current) return;

    const itemDisplayContainer = itemListRef.current;
    const itemContainer = document.createElement("span");
    itemContainer.className = styles.itemContainerMeasure;
    document.body.appendChild(itemContainer);

    const displayItemNames = [];
    if (itemNames.length === 1) {
      setAdjustedItems(itemNames[0]);
    }
    for (let i = 0; i < itemNames.length; i++) {
      displayItemNames.push(itemNames[i]);
      itemContainer.textContent = displayItemNames.join("; ");
      if (itemContainer.offsetWidth > itemDisplayContainer.offsetWidth) {
        displayItemNames.pop();
        break;
      }
    }
    const overFlowingItems = itemNames.length - displayItemNames.length;
    let orderHistoryItems = displayItemNames.join("; ");
    if (overFlowingItems > 0) {
      orderHistoryItems += ` and ${overFlowingItems} more item${overFlowingItems > 1 ? "s" : ""}.`;
    }

    setAdjustedItems(orderHistoryItems);
    document.body.removeChild(itemContainer);
  }, [order?.items]);

  useEffect(() => {
    adjustCardItemNames();
    const handleResize = () => {
      adjustCardItemNames();
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [adjustCardItemNames]);

  const shouldShowReorderButton = order?.items.some(
    (item) =>
      item.issueType === "None" ||
      item.issueType === "ItemPriceMismatched" ||
      item.issueType === "ModifierPriceMismatched",
  );

  const onClick = useCallback(() => {
    navigate("/orders/past/" + order?.id, {
      state: {
        order: order,
        shouldShowReorderButton: shouldShowReorderButton,
      },
    });
    trackEvent(appInsights, appInsightsEvents.Usage.Actions.NavigateToOrderSummary, context, {
      selectedOrderId: order?.id,
    });
  }, [appInsights, context, order?.id, navigate, shouldShowReorderButton]);

  const item = order?.items && order.items.length > 1 ? "items" : "item";
  const ariaItemNames = order?.items
    .map((orderItem) => {
      if (!orderItem.name) return undefined;
      const accessibleLabel = helperUtils.getDietaryLabelAccessibleStr(orderItem);
      return accessibleLabel ? `${orderItem.name} ${accessibleLabel}` : orderItem.name;
    })
    .filter(Boolean)
    .join("; ");
  const ariaLabel = `Order ${order?.orderNumber} from ${order?.cafeName} 
  ${[...new Set(order?.items.map((orderItem) => orderItem.stationName))]} ${helperUtils.getDateFromTimestamp(
    order?.timeStamp,
  )} ${order?.items.length} ${item}${ariaItemNames ? ": " + ariaItemNames : ""} ${order?.totalAmount.currencyUnit} ${
    order?.totalAmount.amount
  } Tap to view order summary.`;

  return (
    <>
      {isLoading && (
        <div className={styles.loaderOverlay}>
          <LoadingScreen />
        </div>
      )}
      <>
        {order && (
          <div
            className={styles.container}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.code === "Space") onClick();
            }}
            data-testid={`orderHistoryCard-${order?.id}`}
            tabIndex={0}
            role="link"
            aria-label={ariaLabel}
          >
            <div className={styles.orderSection}>
              <Subtitle2 wrap={true} truncate className={styles.text}>
                {order.cafeName}:{" "}
                {[...new Set(order.items.map((item) => item.stationName))].join(", ")}
              </Subtitle2>
              <Subtitle2 wrap={false} truncate className={styles.smallText}>
                {helperUtils.getDateFromTimestamp(order.timeStamp)} &middot; Order #
                {order?.orderNumber}
              </Subtitle2>
              <span ref={itemListRef} className={styles.price}>
                {adjustedItems}
              </span>
              <span className={styles.price}>
                {order?.totalAmount.currencyUnit}
                {order?.totalAmount.amount}
              </span>
            </div>
            <div className={styles.rightAlignment}>
              <div className={styles.viewDetail}>
                <Button
                  className={styles.viewDetailsButton}
                  onClick={onClick}
                  data-testid="viewDetailsButton"
                >
                  View Details
                </Button>
              </div>
              <div className={styles.reorderButton}>
                <Button
                  className={styles.rightAlignmentButton}
                  disabled={!reorderingEnabled || !shouldShowReorderButton}
                  shape="rounded"
                  onClick={onReorder}
                  data-testid="reorderButton"
                  type="submit"
                  appearance="primary"
                >
                  <Subtitle2>Reorder</Subtitle2>
                </Button>
                {dialogProps && order?.cafeId !== currentCart?.cafeId && currentCart! && (
                  <ConfirmationDialog {...dialogProps} />
                )}
              </div>
            </div>
          </div>
        )}
        {isDialogOpen && (
          <div className={styles.dialogOverlay}>
            <ReusableDialog
              isOpen={isDialogOpen}
              onClose={() => setDialogOpen(false)}
              title={dialogTitle}
              message={dialogMessage}
              dialogButton={dialogButtonText}
              dialogAction={dialogAction}
              cancelButton="Cancel"
              dismissAction={() => setDialogOpen(false)}
            />
          </div>
        )}
      </>
    </>
  );
};
